import React, { useState, useRef } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  Box,
  Typography,
  LinearProgress,
  Alert,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";
import moment from "moment";
import { Timestamp, collection, getDocs } from "firebase/firestore";
import ModalDialogTitle from "../common/ModalDialogTitle";
import { createTpCoordinatorUser, db } from "../../config/firebase";
import { downloadExcelTemplateWithDropdown } from "../../utils/exportTemplate";

const DEFAULT_PASSWORD = "TpCoord26!$?";

// Column order matches the coordinator roster spreadsheet partners already
// send in: Partner, Last, First, Email, Tartan ID, Phone, Extension.
const TEMPLATE_FIELDS = [
  "Partner",
  "Last",
  "First",
  "Email",
  "Tartan ID",
  "Phone",
  "Extension",
];

// Match a header regardless of spacing/underscores/case, e.g.
// "Tartan ID" / "tartan_id" / "TartanID" all match.
const normalizeHeader = (header) =>
  String(header ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

// Tartan IDs are always 7 digits — pad with leading zeros if shorter.
// Non-numeric placeholders (e.g. "Requested") are already longer than 7
// characters, so padStart leaves them unchanged.
const padTartanId = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return trimmed;
  return trimmed.padStart(7, "0");
};

const formatDateValue = (value) => {
  if (value === null || value === undefined || value === "") return null;

  if (value instanceof Date) return Timestamp.fromDate(value);

  if (typeof value === "number") {
    // Excel date serial
    const excelEpoch = new Date(1900, 0, 1);
    const adjustedDays = value - 2;
    const date = new Date(
      excelEpoch.getTime() + adjustedDays * 86400000,
    );
    return Timestamp.fromDate(date);
  }

  if (typeof value === "string") {
    const parsed = moment(value);
    if (parsed.isValid()) return Timestamp.fromDate(parsed.toDate());
  }

  return null;
};

const initialResults = { successful: 0, failed: 0, errors: [] };

const CoordinatorBulkUpload = ({ onComplete }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [total, setTotal] = useState(0);
  const [processed, setProcessed] = useState(0);
  const [results, setResults] = useState(initialResults);
  const [parseError, setParseError] = useState("");
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const fileInputRef = useRef(null);

  const resetState = () => {
    setFile(null);
    setTotal(0);
    setProcessed(0);
    setResults(initialResults);
    setParseError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleOpen = () => {
    resetState();
    setOpen(true);
  };

  const handleClose = () => {
    if (uploading) return; // block close while a batch is in flight
    setOpen(false);
    if (results.successful > 0 && onComplete) onComplete();
  };

  const handleFileChange = (e) => {
    setParseError("");
    setFile(e.target.files[0] || null);
  };

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const partnersSnapshot = await getDocs(collection(db, "TpPartners"));
      const partnerNames = partnersSnapshot.docs
        .map((d) => d.data())
        .filter((p) => p.Active_yn === "y" && p.Tech_Prep_Partner)
        .map((p) => p.Tech_Prep_Partner)
        .sort((a, b) => a.localeCompare(b));

      // Column 1 = "Partner", the first entry in TEMPLATE_FIELDS.
      await downloadExcelTemplateWithDropdown(
        TEMPLATE_FIELDS,
        "coordinators-template",
        { 1: partnerNames },
      );
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleUpload = () => {
    if (!file) return;
    setParseError("");
    setResults(initialResults);
    setUploading(true);

    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        const headers = rows[0] || [];
        const normalizedHeaders = headers.map(normalizeHeader);

        const emailIdx = normalizedHeaders.findIndex((h) =>
          h.includes("email"),
        );
        const firstNameIdx = normalizedHeaders.findIndex(
          (h) => h === "first",
        );
        const lastNameIdx = normalizedHeaders.findIndex((h) => h === "last");
        const partnerIdx = normalizedHeaders.findIndex(
          (h) => h === "partner",
        );
        const tartanIdIdx = normalizedHeaders.findIndex(
          (h) => h === "tartanid",
        );
        const phoneIdx = normalizedHeaders.findIndex((h) => h === "phone");
        const extensionIdx = normalizedHeaders.findIndex(
          (h) => h === "extension",
        );

        if (
          emailIdx === -1 ||
          firstNameIdx === -1 ||
          lastNameIdx === -1 ||
          partnerIdx === -1
        ) {
          setParseError(
            "The file must include 'Partner', 'First', 'Last', and 'Email' columns.",
          );
          setUploading(false);
          return;
        }

        const dataRows = rows
          .slice(1)
          .filter((row) => row[emailIdx] && String(row[emailIdx]).trim());

        setTotal(dataRows.length);
        setProcessed(0);

        // Detect duplicate emails within the file itself (case-insensitive):
        // map each lowercased email -> the first row number it appeared on.
        const firstSeenRowByEmail = new Map();
        dataRows.forEach((row, i) => {
          const emailLower = String(row[emailIdx]).trim().toLowerCase();
          if (!firstSeenRowByEmail.has(emailLower)) {
            firstSeenRowByEmail.set(emailLower, i + 2);
          }
        });

        // Fetch every email already in the users collection so we can catch
        // collisions Firebase Auth's own uniqueness check would miss — e.g. a
        // users doc left behind after its Auth account was deleted, or a doc
        // created by a plain Firestore import that never went through Auth.
        const existingUsersSnapshot = await getDocs(collection(db, "users"));
        const existingEmails = new Set(
          existingUsersSnapshot.docs
            .map((d) => String(d.data().email || "").trim().toLowerCase())
            .filter(Boolean),
        );

        // Every Partner value must match an active TpPartners record — the
        // Add/Edit form's Partner dropdown only ever offers active partners,
        // so bulk upload holds rows to the same standard rather than
        // creating coordinators tied to a partner name that doesn't exist.
        const partnersSnapshot = await getDocs(collection(db, "TpPartners"));
        const activePartnerByLowerName = new Map();
        partnersSnapshot.docs.forEach((d) => {
          const p = d.data();
          if (p.Active_yn === "y" && p.Tech_Prep_Partner) {
            activePartnerByLowerName.set(
              p.Tech_Prep_Partner.trim().toLowerCase(),
              p.Tech_Prep_Partner,
            );
          }
        });

        let successful = 0;
        let failed = 0;
        const errors = [];

        for (let i = 0; i < dataRows.length; i++) {
          const row = dataRows[i];
          const email = String(row[emailIdx]).trim();
          const emailLower = email.toLowerCase();
          const rowNum = i + 2;
          const firstSeenRow = firstSeenRowByEmail.get(emailLower);
          const firstName = String(row[firstNameIdx] ?? "").trim();
          const lastName = String(row[lastNameIdx] ?? "").trim();
          const name = `${firstName} ${lastName}`.trim();
          const partnerInput = String(row[partnerIdx] ?? "").trim();
          const matchedPartner = activePartnerByLowerName.get(
            partnerInput.toLowerCase(),
          );
          const tartanId =
            tartanIdIdx !== -1
              ? padTartanId(row[tartanIdIdx])
              : "";
          const phone = phoneIdx !== -1 ? String(row[phoneIdx] ?? "").trim() : "";
          const extension =
            extensionIdx !== -1 ? String(row[extensionIdx] ?? "").trim() : "";

          const userData = {
            firstName,
            lastName,
            name,
            tp_partner: matchedPartner || "",
            Tartan_Id: tartanId,
            phone,
            extension,
            techPrepFlag: true,
            userTypes: { coordinator: true },
          };

          headers.forEach((header, idx) => {
            if (
              [
                emailIdx,
                firstNameIdx,
                lastNameIdx,
                partnerIdx,
                tartanIdIdx,
                phoneIdx,
                extensionIdx,
              ].includes(idx)
            ) {
              return;
            }
            if (!header) return;

            let value = row[idx];
            if (value === "NULL" || value === undefined) value = "";

            if (normalizeHeader(header).includes("date")) {
              value = formatDateValue(value);
            }

            userData[header] = value;
          });

          try {
            if (!name) throw new Error("First/Last name required");

            if (!partnerInput) {
              throw new Error("Partner is required");
            }
            if (!matchedPartner) {
              throw new Error(
                `Partner "${partnerInput}" was not found among active partners. Skipped.`,
              );
            }

            if (firstSeenRow !== rowNum) {
              throw new Error(
                `Duplicate email in file — first appears on row ${firstSeenRow}. Skipped.`,
              );
            }

            if (existingEmails.has(emailLower)) {
              throw new Error(
                "A user with this email already exists in the system. Skipped.",
              );
            }

            const result = await createTpCoordinatorUser(
              email,
              DEFAULT_PASSWORD,
              userData,
            );

            if (!result.success) {
              throw new Error(result.message || "Unknown error");
            }
            successful++;
            // Prevent a later duplicate row in the same file from also
            // passing the existing-email check once this one succeeds.
            existingEmails.add(emailLower);
          } catch (err) {
            failed++;
            errors.push(`Row ${rowNum}: ${email} — ${err.message}`);
          }

          setProcessed(i + 1);
          setResults({ successful, failed, errors: [...errors] });
        }
      } catch (err) {
        setParseError(`Error reading file: ${err.message}`);
      } finally {
        setUploading(false);
      }
    };

    reader.onerror = () => {
      setParseError("Error reading file.");
      setUploading(false);
    };

    reader.readAsArrayBuffer(file);
  };

  const progressPct = total > 0 ? Math.round((processed / total) * 100) : 0;
  const done = total > 0 && processed === total && !uploading;

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<CloudUploadIcon />}
        onClick={handleOpen}
      >
        Bulk Upload Coordinators
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <ModalDialogTitle onClose={handleClose}>
          Bulk Upload Coordinators
        </ModalDialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload an Excel file to create Tech Prep Coordinator accounts.
            The file must include <strong>Partner</strong>,{" "}
            <strong>First</strong>, <strong>Last</strong>, and{" "}
            <strong>Email</strong> columns; <strong>Tartan ID</strong>,{" "}
            <strong>Phone</strong>, and <strong>Extension</strong> are
            optional. Every Partner value must match an active partner in
            the Partners list — the downloaded template restricts that
            column to a dropdown of active partners. Every user created
            will be flagged as a Coordinator and Tech Prep user, with a
            default password of <strong>{DEFAULT_PASSWORD}</strong>. Rows
            with a duplicate email (within the file or already in the
            system) or an unrecognized Partner are skipped and reported
            below rather than creating a duplicate or unlinked account.
          </Typography>

          <Button
            variant="outlined"
            color="success"
            startIcon={<FontAwesomeIcon icon={faFileExcel} />}
            onClick={handleDownloadTemplate}
            disabled={downloadingTemplate}
            sx={{ mb: 2 }}
          >
            {downloadingTemplate ? "Preparing…" : "Download Template"}
          </Button>

          {parseError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {parseError}
            </Alert>
          )}

          {!uploading && total === 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 2,
              }}
            >
              <input
                type="file"
                accept=".xlsx,.xls"
                ref={fileInputRef}
                onChange={handleFileChange}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<CloudUploadIcon />}
                disabled={!file}
                onClick={handleUpload}
              >
                Start Upload
              </Button>
            </Box>
          )}

          {(uploading || total > 0) && (
            <Box sx={{ mt: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="body2">
                  {done ? "Upload complete" : "Creating coordinator accounts…"}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {processed} of {total} ({progressPct}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progressPct}
                color={done && results.failed > 0 ? "warning" : "primary"}
                sx={{ height: 10, borderRadius: 5, mb: 2 }}
              />

              <Box sx={{ display: "flex", gap: 3, mb: 1 }}>
                <Typography variant="body2" color="success.main">
                  Successful: {results.successful}
                </Typography>
                <Typography variant="body2" color="error.main">
                  Failed: {results.failed}
                </Typography>
              </Box>

              {results.errors.length > 0 && (
                <Box
                  sx={{
                    maxHeight: 180,
                    overflowY: "auto",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  {results.errors.map((err, idx) => (
                    <Typography
                      key={idx}
                      variant="caption"
                      color="error.main"
                      component="div"
                    >
                      {err}
                    </Typography>
                  ))}
                </Box>
              )}

              {done && (
                <Button
                  sx={{ mt: 2 }}
                  variant="contained"
                  onClick={handleClose}
                >
                  Done
                </Button>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CoordinatorBulkUpload;
